/**
 * Capacitor 原生存储（iOS：UserDefaults / NSUserDefaults），与 WebView localStorage 双写。
 * 用途：审核与真机场景下 Web 存储偶发清空时恢复登录态；保留「首次以原生层打开」时间戳备查。
 * 浏览器 / 非原生：所有方法立即 no-op，不打包失败。
 */
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const K_SESSION = 'WK_SESSION_MIRROR_V1';
const K_REMEMBER = 'WK_REMEMBER_MIRROR_V1';
const K_FIRST_NATIVE_OPEN = 'WK_FIRST_NATIVE_OPEN_ISO_V1';

export function isCapacitorNativeApp() {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
}

/** 启动最早调用：写首次打开时间；若 localStorage 丢则尝试从原生恢复 _pwd / remember */
export async function bootstrapNativePreferences() {
  if (!isCapacitorNativeApp()) return;
  try {
    const { value: first } = await Preferences.get({ key: K_FIRST_NATIVE_OPEN });
    if (!first) {
      await Preferences.set({ key: K_FIRST_NATIVE_OPEN, value: new Date().toISOString() });
    }

    const { value: session } = await Preferences.get({ key: K_SESSION });
    if (session) {
      try {
        if (!localStorage.getItem('_pwd')) {
          localStorage.setItem('_pwd', session);
        }
      } catch (e) {
        /* ignore */
      }
    }

    const { value: remember } = await Preferences.get({ key: K_REMEMBER });
    if (remember) {
      try {
        if (!localStorage.getItem('remember')) {
          localStorage.setItem('remember', remember);
        }
      } catch (e) {
        /* ignore */
      }
    }
  } catch (e) {
    console.warn('[bootstrapNativePreferences]', e);
  }
}

/** 与当前 localStorage 同步（登录成功、改 remember 后调用） */
export async function mirrorAuthToNative() {
  if (!isCapacitorNativeApp()) return;
  try {
    const s = (() => {
      try {
        return localStorage.getItem('_pwd');
      } catch (e) {
        return null;
      }
    })();
    const r = (() => {
      try {
        return localStorage.getItem('remember');
      } catch (e) {
        return null;
      }
    })();

    if (s) await Preferences.set({ key: K_SESSION, value: s });
    else await Preferences.remove({ key: K_SESSION });

    if (r) await Preferences.set({ key: K_REMEMBER, value: r });
    else await Preferences.remove({ key: K_REMEMBER });
  } catch (e) {
    console.warn('[mirrorAuthToNative]', e);
  }
}

/** 登出 / VIP 拦截 / 401 等与 removeItem('_pwd') 同步清理原生镜像 */
export async function clearNativeAuthMirror() {
  if (!isCapacitorNativeApp()) return;
  try {
    await Preferences.remove({ key: K_SESSION });
    await Preferences.remove({ key: K_REMEMBER });
  } catch (e) {
    console.warn('[clearNativeAuthMirror]', e);
  }
}
